import React, { useRef, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';
import JSZip from 'jszip';
import { Download, Printer, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

/**
 * Trabadju — Campanha de Flyers A5 (Cabo Verde)
 *
 * 6 designs · cada um com FRENTE (empregador) + VERSO (candidato)
 * A5 = 148 × 210 mm @ 300 DPI = 1748 × 2480 px
 *
 * Princípios:
 *  - Imagens IA fotorrealistas com cabo-verdianos profissionais (top da página)
 *  - Texto sempre em painel colorido sólido (alta legibilidade garantida)
 *  - Margem interior de segurança de 150px em todos os lados
 *  - QR code REAL gerado em runtime (qrcode.react) — válido ao scan
 *  - Cores de marca: azul, indigo, âmbar/laranja (candidato), navy
 */

const URL_HOME = 'https://trabadju.cv';
const URL_EMP = 'https://trabadju.cv/register';
const URL_CAN = 'https://trabadju.cv/candidate/register';

// ───────────────────────── Tokens ─────────────────────────
const W = 1748;
const H = 2480;
const SAFE = 150; // safe inner margin (≈12.7mm at 300dpi)

const COLORS = {
  navy: '#0b1e44',
  blue: '#1d4ed8',
  blueDeep: '#1e40af',
  indigo: '#3730a3',
  amber: '#b45309',
  amberSoft: '#f59e0b',
  cream: '#fff8ec',
  ivory: '#faf6ef',
  white: '#ffffff',
  ink: '#0f172a',
};

const FONT = '"Helvetica Neue", "Inter", Arial, sans-serif';

// ───────────────────────── Shared atoms ─────────────────────────

function Brand({ color = COLORS.white, sub = COLORS.amberSoft }) {
  return (
    <div>
      <div style={{ fontSize: 96, fontWeight: 900, letterSpacing: '-3px', color, lineHeight: 1, fontFamily: FONT }}>
        Trabadju
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '12px', color: sub, marginTop: 6, fontFamily: FONT }}>
        CABO VERDE
      </div>
    </div>
  );
}

function QrBlock({ value = URL_HOME, label = 'Scaneie e visite', textColor = COLORS.ink, panelBg = COLORS.white }) {
  return (
    <div
      style={{
        background: panelBg,
        padding: 28,
        borderRadius: 22,
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
      }}
    >
      <QRCodeSVG value={value} size={300} level="H" includeMargin={false} bgColor="#ffffff" fgColor={COLORS.navy} />
      <div style={{ fontSize: 22, fontWeight: 700, color: textColor, fontFamily: FONT, textAlign: 'center', lineHeight: 1.2 }}>
        {label}
      </div>
    </div>
  );
}

function FlyerFrame({ bg = COLORS.white, children, accent }) {
  // The outer container clips anything that overflows so nothing is cut at print
  return (
    <div
      style={{
        width: W,
        height: H,
        background: bg,
        position: 'relative',
        overflow: 'hidden',
        fontFamily: FONT,
      }}
    >
      {accent && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 22,
            background: accent,
          }}
        />
      )}
      {children}
    </div>
  );
}

function HeroImage({ src, height = 1280, overlay }) {
  return (
    <div style={{ position: 'relative', width: '100%', height, overflow: 'hidden' }}>
      <img
        src={src}
        alt=""
        crossOrigin="anonymous"
        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
      />
      {overlay && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: overlay,
          }}
        />
      )}
    </div>
  );
}

// Generic reusable panel used at bottom of every flyer
function TextPanel({ bg, children, paddingTop = 80, paddingBottom = SAFE }) {
  return (
    <div
      style={{
        background: bg,
        padding: `${paddingTop}px ${SAFE}px ${paddingBottom}px`,
        boxSizing: 'border-box',
        minHeight: H - 1280,
        display: 'flex',
        flexDirection: 'column',
        gap: 36,
      }}
    >
      {children}
    </div>
  );
}

function FooterContact({ color = 'rgba(255,255,255,0.85)' }) {
  return (
    <div
      style={{
        fontSize: 22,
        fontWeight: 600,
        color,
        fontFamily: FONT,
        display: 'flex',
        gap: 36,
        flexWrap: 'wrap',
      }}
    >
      <span>trabadju.cv</span>
      <span>·</span>
      <span>@trabadju.cv</span>
      <span>·</span>
      <span>info@trabadju.cv</span>
    </div>
  );
}

// ───────────────────────── DESIGN 1 · O TELEMÓVEL ─────────────────────────
const D1_FRENTE = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.navy} accent={COLORS.amberSoft}>
      <HeroImage
        src="/flyers/img/01_mobile_employer.png"
        height={1280}
        overlay="linear-gradient(180deg, rgba(11,30,68,0) 55%, rgba(11,30,68,0.95) 100%)"
      />
      <TextPanel bg={COLORS.navy}>
        <Brand />
        <div style={{ fontSize: 124, fontWeight: 900, color: COLORS.white, lineHeight: 1.02, letterSpacing: '-3px' }}>
          Encontre o<br />talento certo.<br />
          <span style={{ color: COLORS.amberSoft }}>Em minutos.</span>
        </div>
        <div style={{ fontSize: 36, color: 'rgba(255,255,255,0.92)', fontWeight: 500, lineHeight: 1.3 }}>
          Publique vagas e receba candidaturas em todas as ilhas de Cabo Verde.
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 40, marginTop: 'auto' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 28, color: COLORS.amberSoft, fontWeight: 800, letterSpacing: '4px', marginBottom: 10 }}>
              SOU EMPREGADOR
            </div>
            <div style={{ fontSize: 40, color: COLORS.white, fontWeight: 700, lineHeight: 1.15 }}>
              Comece em 2 minutos.<br />Pague apenas pelo que publica.
            </div>
            <div style={{ marginTop: 24 }}>
              <FooterContact />
            </div>
          </div>
          <QrBlock value={URL_EMP} label="Registar empresa" />
        </div>
      </TextPanel>
    </FlyerFrame>
  </div>
));

const D1_VERSO = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.amber} accent={COLORS.white}>
      <HeroImage
        src="/flyers/img/01_mobile_candidate.png"
        height={1280}
        overlay="linear-gradient(180deg, rgba(180,83,9,0) 55%, rgba(180,83,9,0.95) 100%)"
      />
      <TextPanel bg={COLORS.amber}>
        <Brand sub={COLORS.cream} />
        <div style={{ fontSize: 124, fontWeight: 900, color: COLORS.white, lineHeight: 1.02, letterSpacing: '-3px' }}>
          O seu próximo<br />emprego<br />
          <span style={{ color: COLORS.cream }}>está aqui.</span>
        </div>
        <div style={{ fontSize: 36, color: 'rgba(255,248,236,0.95)', fontWeight: 500, lineHeight: 1.3 }}>
          Pesquise vagas, candidate-se de graça e receba alertas por email.
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 40, marginTop: 'auto' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 28, color: COLORS.cream, fontWeight: 800, letterSpacing: '4px', marginBottom: 10 }}>
              SOU CANDIDATO
            </div>
            <div style={{ fontSize: 40, color: COLORS.white, fontWeight: 700, lineHeight: 1.15 }}>
              Candidatar é gratuito.<br />Crie o seu perfil hoje.
            </div>
            <div style={{ marginTop: 24 }}>
              <FooterContact color="rgba(255,248,236,0.92)" />
            </div>
          </div>
          <QrBlock value={URL_CAN} label="Criar perfil candidato" />
        </div>
      </TextPanel>
    </FlyerFrame>
  </div>
));

// ───────────────────────── DESIGN 2 · CARREIRA ─────────────────────────
const D2_FRENTE = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.indigo}>
      <div style={{ display: 'flex', width: W, height: H }}>
        <div style={{ width: '50%', height: '100%', overflow: 'hidden' }}>
          <img
            src="/flyers/img/02_career_employer.png"
            alt=""
            crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
          />
        </div>
        <div
          style={{
            width: '50%',
            height: '100%',
            background: COLORS.indigo,
            color: COLORS.white,
            padding: `${SAFE}px ${SAFE}px ${SAFE}px 120px`,
            display: 'flex',
            flexDirection: 'column',
            gap: 32,
            boxSizing: 'border-box',
          }}
        >
          <Brand sub={COLORS.amberSoft} />
          <div style={{ fontSize: 92, fontWeight: 900, lineHeight: 1.02, letterSpacing: '-2px' }}>
            Construa<br />a sua<br />
            <span style={{ color: COLORS.amberSoft }}>equipa.</span>
          </div>
          <div style={{ fontSize: 32, color: 'rgba(255,255,255,0.92)', lineHeight: 1.35, fontWeight: 500 }}>
            A plataforma #1 de emprego em Cabo Verde. Sem mensalidades — pague apenas pelo anúncio que publica.
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 18,
              padding: 24,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 18,
            }}
          >
            {[
              ['Anúncio simples', '1 500 CVE'],
              ['Em destaque', '3 000 CVE'],
              ['Pack 5', '6 000 CVE'],
              ['Pack 15', '15 000 CVE'],
            ].map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.72)', fontWeight: 600 }}>{k}</div>
                <div style={{ fontSize: 32, color: COLORS.white, fontWeight: 800 }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 'auto' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.amberSoft, letterSpacing: '4px', marginBottom: 14 }}>
              SOU EMPREGADOR
            </div>
            <QrBlock value={URL_EMP} label="Registar empresa" />
            <div style={{ marginTop: 22 }}>
              <FooterContact />
            </div>
          </div>
        </div>
      </div>
    </FlyerFrame>
  </div>
));

const D2_VERSO = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.amber}>
      <div style={{ display: 'flex', width: W, height: H, flexDirection: 'row-reverse' }}>
        <div style={{ width: '50%', height: '100%', overflow: 'hidden' }}>
          <img
            src="/flyers/img/02_career_candidate.png"
            alt=""
            crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
          />
        </div>
        <div
          style={{
            width: '50%',
            height: '100%',
            background: COLORS.amber,
            color: COLORS.white,
            padding: `${SAFE}px 120px ${SAFE}px ${SAFE}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: 32,
            boxSizing: 'border-box',
          }}
        >
          <Brand sub={COLORS.cream} />
          <div style={{ fontSize: 92, fontWeight: 900, lineHeight: 1.02, letterSpacing: '-2px' }}>
            Construa<br />o seu<br />
            <span style={{ color: COLORS.cream }}>futuro.</span>
          </div>
          <div style={{ fontSize: 32, color: 'rgba(255,248,236,0.95)', lineHeight: 1.35, fontWeight: 500 }}>
            Candidate-se a vagas em todas as ilhas de Cabo Verde. <strong>É 100% gratuito.</strong>
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: 18,
              padding: 24,
            }}
          >
            <div style={{ fontSize: 26, color: COLORS.white, fontWeight: 800, marginBottom: 12 }}>O que pode fazer:</div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {['Criar perfil + upload de CV', 'Pesquisar por categoria e ilha', 'Receber alertas por email', 'Candidatar com um clique'].map((t) => (
                <li key={t} style={{ fontSize: 26, fontWeight: 600, color: COLORS.white }}>
                  ✓ {t}
                </li>
              ))}
            </ul>
          </div>
          <div style={{ marginTop: 'auto' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.cream, letterSpacing: '4px', marginBottom: 14 }}>
              SOU CANDIDATO
            </div>
            <QrBlock value={URL_CAN} label="Criar perfil candidato" />
            <div style={{ marginTop: 22 }}>
              <FooterContact color="rgba(255,248,236,0.95)" />
            </div>
          </div>
        </div>
      </div>
    </FlyerFrame>
  </div>
));

// ───────────────────────── DESIGN 3 · EMPRESA CONTRATA ─────────────────────────
const D3_FRENTE = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.white} accent={COLORS.blue}>
      <div style={{ padding: `${SAFE}px ${SAFE}px 60px`, display: 'flex', flexDirection: 'column', gap: 28 }}>
        <Brand color={COLORS.blue} sub={COLORS.navy} />
        <div style={{ fontSize: 112, fontWeight: 900, color: COLORS.navy, lineHeight: 1.02, letterSpacing: '-3px' }}>
          Publique vagas.<br />
          <span style={{ color: COLORS.blue }}>Receba candidatos.</span>
        </div>
        <div style={{ fontSize: 34, color: '#334155', fontWeight: 500, lineHeight: 1.35 }}>
          Painel simples, factura PDF automática e pagamento Vinti4 ou cartão internacional.
        </div>
      </div>
      <div style={{ width: '100%', height: 980, overflow: 'hidden' }}>
        <img
          src="/flyers/img/03_hire_employer.png"
          alt=""
          crossOrigin="anonymous"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
        />
      </div>
      <div
        style={{
          background: COLORS.blue,
          color: COLORS.white,
          padding: `60px ${SAFE}px ${SAFE}px`,
          display: 'flex',
          alignItems: 'center',
          gap: 48,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '4px', color: COLORS.amberSoft, marginBottom: 12 }}>
            SOU EMPREGADOR
          </div>
          <div style={{ fontSize: 44, fontWeight: 800, lineHeight: 1.1, marginBottom: 18 }}>
            A partir de<br />
            <span style={{ fontSize: 96, color: COLORS.amberSoft }}>1 500 CVE</span><br />por anúncio.
          </div>
          <FooterContact />
        </div>
        <QrBlock value={URL_EMP} label="Registar empresa" />
      </div>
    </FlyerFrame>
  </div>
));

const D3_VERSO = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.white} accent={COLORS.amber}>
      <div style={{ padding: `${SAFE}px ${SAFE}px 60px`, display: 'flex', flexDirection: 'column', gap: 28 }}>
        <Brand color={COLORS.amber} sub={COLORS.navy} />
        <div style={{ fontSize: 112, fontWeight: 900, color: COLORS.navy, lineHeight: 1.02, letterSpacing: '-3px' }}>
          Candidate-se<br />
          <span style={{ color: COLORS.amber }}>gratuitamente.</span>
        </div>
        <div style={{ fontSize: 34, color: '#334155', fontWeight: 500, lineHeight: 1.35 }}>
          Perfil profissional, CV em PDF e candidatura com um clique. Sem custos para si.
        </div>
      </div>
      <div style={{ width: '100%', height: 980, overflow: 'hidden' }}>
        <img
          src="/flyers/img/03_hire_candidate.png"
          alt=""
          crossOrigin="anonymous"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
        />
      </div>
      <div
        style={{
          background: COLORS.amber,
          color: COLORS.white,
          padding: `60px ${SAFE}px ${SAFE}px`,
          display: 'flex',
          alignItems: 'center',
          gap: 48,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '4px', color: COLORS.cream, marginBottom: 12 }}>
            SOU CANDIDATO
          </div>
          <div style={{ fontSize: 56, fontWeight: 800, lineHeight: 1.1, marginBottom: 18 }}>
            <span style={{ fontSize: 96, color: COLORS.cream }}>0 CVE</span><br />para candidatos.
          </div>
          <FooterContact color="rgba(255,248,236,0.95)" />
        </div>
        <QrBlock value={URL_CAN} label="Criar perfil candidato" />
      </div>
    </FlyerFrame>
  </div>
));

// ───────────────────────── DESIGN 4 · CABO VERDE NO MAPA ─────────────────────────
const D4_FRENTE = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.blueDeep} accent={COLORS.amberSoft}>
      <div style={{ position: 'relative', width: '100%', height: 1400, overflow: 'hidden' }}>
        <img
          src="/flyers/img/04_islands_employer.png"
          alt=""
          crossOrigin="anonymous"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(30,64,175,0.55) 0%, rgba(30,64,175,0.2) 35%, rgba(11,30,68,0.95) 100%)',
          }}
        />
        <div style={{ position: 'absolute', top: SAFE, left: SAFE, right: SAFE }}>
          <Brand />
          <div style={{ marginTop: 40, fontSize: 104, fontWeight: 900, color: COLORS.white, letterSpacing: '-3px', lineHeight: 1.02 }}>
            10 ilhas.<br />
            <span style={{ color: COLORS.amberSoft }}>1 plataforma.</span>
          </div>
        </div>
      </div>
      <TextPanel bg={COLORS.navy} paddingTop={60}>
        <div style={{ fontSize: 40, color: COLORS.white, fontWeight: 600, lineHeight: 1.3 }}>
          Alcance candidatos em <strong>toda a Cabo Verde</strong> — da Praia ao Mindelo, Sal, Boa Vista, Fogo, Santo Antão, São Nicolau, Maio, Brava, Santiago e Santa Luzia.
        </div>
        <div
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 18,
            padding: 28,
            display: 'flex',
            justifyContent: 'space-around',
            gap: 24,
          }}
        >
          {[['Pay-per-post', 'sem mensalidades'], ['Vinti4 + Stripe', 'pagamento local'], ['Factura PDF', 'automática']].map(([k, v]) => (
            <div key={k} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 30, color: COLORS.amberSoft, fontWeight: 800 }}>{k}</div>
              <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.85)', fontWeight: 500, marginTop: 4 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 40, marginTop: 'auto' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 26, color: COLORS.amberSoft, fontWeight: 800, letterSpacing: '4px', marginBottom: 12 }}>
              SOU EMPREGADOR
            </div>
            <div style={{ fontSize: 38, color: COLORS.white, fontWeight: 700, lineHeight: 1.2 }}>
              Publique a sua vaga hoje.
            </div>
            <div style={{ marginTop: 22 }}>
              <FooterContact />
            </div>
          </div>
          <QrBlock value={URL_EMP} label="Registar empresa" />
        </div>
      </TextPanel>
    </FlyerFrame>
  </div>
));

const D4_VERSO = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.amber} accent={COLORS.white}>
      <div style={{ position: 'relative', width: '100%', height: 1400, overflow: 'hidden' }}>
        <img
          src="/flyers/img/04_islands_candidate.png"
          alt=""
          crossOrigin="anonymous"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(180,83,9,0.35) 0%, rgba(180,83,9,0.15) 35%, rgba(180,83,9,0.97) 100%)',
          }}
        />
        <div style={{ position: 'absolute', top: SAFE, left: SAFE, right: SAFE }}>
          <Brand sub={COLORS.cream} />
          <div style={{ marginTop: 40, fontSize: 104, fontWeight: 900, color: COLORS.white, letterSpacing: '-3px', lineHeight: 1.02 }}>
            Oportunidades<br />em <span style={{ color: COLORS.cream }}>todas</span><br />as ilhas.
          </div>
        </div>
      </div>
      <TextPanel bg={COLORS.amber} paddingTop={60}>
        <div style={{ fontSize: 40, color: COLORS.white, fontWeight: 600, lineHeight: 1.3 }}>
          Centenas de vagas em <strong>Cabo Verde inteiro</strong>. Pesquise por categoria, ilha e salário — e candidate-se em segundos.
        </div>
        <div
          style={{
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.22)',
            borderRadius: 18,
            padding: 28,
            display: 'flex',
            justifyContent: 'space-around',
            gap: 24,
          }}
        >
          {[['100% grátis', 'para candidatos'], ['Alertas por email', 'novas vagas'], ['Favoritos', 'guarde e veja depois']].map(([k, v]) => (
            <div key={k} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 30, color: COLORS.cream, fontWeight: 800 }}>{k}</div>
              <div style={{ fontSize: 22, color: 'rgba(255,248,236,0.92)', fontWeight: 500, marginTop: 4 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 40, marginTop: 'auto' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 26, color: COLORS.cream, fontWeight: 800, letterSpacing: '4px', marginBottom: 12 }}>
              SOU CANDIDATO
            </div>
            <div style={{ fontSize: 38, color: COLORS.white, fontWeight: 700, lineHeight: 1.2 }}>
              Comece o seu perfil hoje.
            </div>
            <div style={{ marginTop: 22 }}>
              <FooterContact color="rgba(255,248,236,0.95)" />
            </div>
          </div>
          <QrBlock value={URL_CAN} label="Criar perfil candidato" />
        </div>
      </TextPanel>
    </FlyerFrame>
  </div>
));

// ───────────────────────── DESIGN 5 · SUCESSO ─────────────────────────
const D5_FRENTE = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.ivory} accent={COLORS.blueDeep}>
      <HeroImage
        src="/flyers/img/05_success_employer.png"
        height={1380}
        overlay="linear-gradient(180deg, rgba(255,255,255,0) 60%, rgba(11,30,68,0.85) 100%)"
      />
      <div style={{ position: 'absolute', top: SAFE, left: SAFE, right: SAFE }}>
        <Brand color={COLORS.blueDeep} sub={COLORS.navy} />
      </div>
      <TextPanel bg={COLORS.navy} paddingTop={60}>
        <div style={{ fontSize: 104, fontWeight: 900, color: COLORS.white, lineHeight: 1.02, letterSpacing: '-3px' }}>
          Histórias de<br />sucesso começam<br />
          <span style={{ color: COLORS.amberSoft }}>com a equipa certa.</span>
        </div>
        <div style={{ fontSize: 34, color: 'rgba(255,255,255,0.92)', fontWeight: 500, lineHeight: 1.3 }}>
          Junte-se aos empregadores que confiam no Trabadju para encontrar talento em Cabo Verde.
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 40, marginTop: 'auto' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 26, color: COLORS.amberSoft, fontWeight: 800, letterSpacing: '4px', marginBottom: 12 }}>
              SOU EMPREGADOR
            </div>
            <div style={{ fontSize: 38, color: COLORS.white, fontWeight: 700, lineHeight: 1.2 }}>
              Publique a primeira vaga.<br />Sem compromisso, sem mensalidade.
            </div>
            <div style={{ marginTop: 22 }}>
              <FooterContact />
            </div>
          </div>
          <QrBlock value={URL_EMP} label="Registar empresa" />
        </div>
      </TextPanel>
    </FlyerFrame>
  </div>
));

const D5_VERSO = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.ivory} accent={COLORS.amber}>
      <HeroImage
        src="/flyers/img/05_success_candidate.png"
        height={1380}
        overlay="linear-gradient(180deg, rgba(255,255,255,0) 60%, rgba(180,83,9,0.92) 100%)"
      />
      <div style={{ position: 'absolute', top: SAFE, left: SAFE, right: SAFE }}>
        <Brand color={COLORS.amber} sub={COLORS.navy} />
      </div>
      <TextPanel bg={COLORS.amber} paddingTop={60}>
        <div style={{ fontSize: 104, fontWeight: 900, color: COLORS.white, lineHeight: 1.02, letterSpacing: '-3px' }}>
          A sua história<br />começa com<br />
          <span style={{ color: COLORS.cream }}>o primeiro clique.</span>
        </div>
        <div style={{ fontSize: 34, color: 'rgba(255,248,236,0.95)', fontWeight: 500, lineHeight: 1.3 }}>
          Crie o seu perfil, carregue o CV e candidate-se em segundos. Gratuito — sempre.
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 40, marginTop: 'auto' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 26, color: COLORS.cream, fontWeight: 800, letterSpacing: '4px', marginBottom: 12 }}>
              SOU CANDIDATO
            </div>
            <div style={{ fontSize: 38, color: COLORS.white, fontWeight: 700, lineHeight: 1.2 }}>
              Encontre o emprego que merece.
            </div>
            <div style={{ marginTop: 22 }}>
              <FooterContact color="rgba(255,248,236,0.95)" />
            </div>
          </div>
          <QrBlock value={URL_CAN} label="Criar perfil candidato" />
        </div>
      </TextPanel>
    </FlyerFrame>
  </div>
));

// ───────────────────────── DESIGN 6 · DOIS LADOS (CLAREZA) ─────────────────────────
const D6_FRENTE = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.blueDeep} accent={COLORS.amberSoft}>
      <div style={{ padding: `${SAFE}px ${SAFE}px 60px` }}>
        <Brand />
      </div>
      <div style={{ width: '100%', height: 1100, overflow: 'hidden', position: 'relative' }}>
        <img
          src="/flyers/img/06_dual_employer.png"
          alt=""
          crossOrigin="anonymous"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(30,64,175,0.92) 0%, rgba(30,64,175,0.6) 50%, rgba(30,64,175,0.0) 100%)' }} />
        <div style={{ position: 'absolute', top: 80, left: SAFE, right: SAFE }}>
          <div style={{ fontSize: 36, color: COLORS.amberSoft, fontWeight: 800, letterSpacing: '6px', marginBottom: 12 }}>
            LADO 1 — EMPREGADOR
          </div>
          <div style={{ fontSize: 124, color: COLORS.white, fontWeight: 900, lineHeight: 1, letterSpacing: '-3px' }}>
            Quero<br />contratar.
          </div>
        </div>
      </div>
      <TextPanel bg={COLORS.blueDeep} paddingTop={50}>
        <div style={{ fontSize: 34, color: 'rgba(255,255,255,0.95)', fontWeight: 500, lineHeight: 1.3 }}>
          <strong style={{ color: COLORS.amberSoft }}>1.</strong> Crie a sua conta empresa.<br />
          <strong style={{ color: COLORS.amberSoft }}>2.</strong> Publique a vaga (a partir de 1 500 CVE).<br />
          <strong style={{ color: COLORS.amberSoft }}>3.</strong> Receba candidaturas no painel.
        </div>
        <div
          style={{
            background: 'rgba(255,255,255,0.10)',
            border: '2px solid rgba(255,255,255,0.25)',
            borderRadius: 22,
            padding: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 32,
            marginTop: 'auto',
          }}
        >
          <div>
            <div style={{ fontSize: 30, color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>Vá para</div>
            <div style={{ fontSize: 52, color: COLORS.white, fontWeight: 900, letterSpacing: '-1px' }}>
              trabadju.cv/register
            </div>
            <div style={{ fontSize: 24, color: COLORS.amberSoft, fontWeight: 700, marginTop: 10 }}>
              ou scaneie o QR ao lado →
            </div>
          </div>
          <QrBlock value={URL_EMP} label="Empresa" />
        </div>
        <FooterContact />
      </TextPanel>
    </FlyerFrame>
  </div>
));

const D6_VERSO = React.forwardRef((_, ref) => (
  <div ref={ref}>
    <FlyerFrame bg={COLORS.amber} accent={COLORS.white}>
      <div style={{ padding: `${SAFE}px ${SAFE}px 60px` }}>
        <Brand sub={COLORS.cream} />
      </div>
      <div style={{ width: '100%', height: 1100, overflow: 'hidden', position: 'relative' }}>
        <img
          src="/flyers/img/06_dual_candidate.png"
          alt=""
          crossOrigin="anonymous"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(180,83,9,0.92) 0%, rgba(180,83,9,0.6) 50%, rgba(180,83,9,0.0) 100%)' }} />
        <div style={{ position: 'absolute', top: 80, left: SAFE, right: SAFE }}>
          <div style={{ fontSize: 36, color: COLORS.cream, fontWeight: 800, letterSpacing: '6px', marginBottom: 12 }}>
            LADO 2 — CANDIDATO
          </div>
          <div style={{ fontSize: 124, color: COLORS.white, fontWeight: 900, lineHeight: 1, letterSpacing: '-3px' }}>
            Quero<br />trabalhar.
          </div>
        </div>
      </div>
      <TextPanel bg={COLORS.amber} paddingTop={50}>
        <div style={{ fontSize: 34, color: 'rgba(255,248,236,0.95)', fontWeight: 500, lineHeight: 1.3 }}>
          <strong style={{ color: COLORS.cream }}>1.</strong> Crie o seu perfil de candidato.<br />
          <strong style={{ color: COLORS.cream }}>2.</strong> Carregue o CV e documentos.<br />
          <strong style={{ color: COLORS.cream }}>3.</strong> Candidate-se às vagas — <strong>de graça</strong>.
        </div>
        <div
          style={{
            background: 'rgba(255,255,255,0.14)',
            border: '2px solid rgba(255,255,255,0.30)',
            borderRadius: 22,
            padding: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 32,
            marginTop: 'auto',
          }}
        >
          <div>
            <div style={{ fontSize: 30, color: 'rgba(255,248,236,0.85)', fontWeight: 600 }}>Vá para</div>
            <div style={{ fontSize: 48, color: COLORS.white, fontWeight: 900, letterSpacing: '-1px' }}>
              trabadju.cv/candidate/register
            </div>
            <div style={{ fontSize: 24, color: COLORS.cream, fontWeight: 700, marginTop: 10 }}>
              ou scaneie o QR ao lado →
            </div>
          </div>
          <QrBlock value={URL_CAN} label="Candidato" />
        </div>
        <FooterContact color="rgba(255,248,236,0.95)" />
      </TextPanel>
    </FlyerFrame>
  </div>
));

// ───────────────────────── Page ─────────────────────────

const DESIGNS = [
  {
    key: 'd1',
    title: 'Design 1 — O Telemóvel',
    subtitle: 'Foco em mobile-first · Cabo Verde digital',
    frente: { component: D1_FRENTE, filename: 'trabadju-D1-frente-empregador.png' },
    verso: { component: D1_VERSO, filename: 'trabadju-D1-verso-candidato.png' },
  },
  {
    key: 'd2',
    title: 'Design 2 — Carreira',
    subtitle: 'Layout vertical 50/50 · imagem + painel de preços',
    frente: { component: D2_FRENTE, filename: 'trabadju-D2-frente-empregador.png' },
    verso: { component: D2_VERSO, filename: 'trabadju-D2-verso-candidato.png' },
  },
  {
    key: 'd3',
    title: 'Design 3 — Empresa Contrata',
    subtitle: 'Híbrido editorial · preço em destaque',
    frente: { component: D3_FRENTE, filename: 'trabadju-D3-frente-empregador.png' },
    verso: { component: D3_VERSO, filename: 'trabadju-D3-verso-candidato.png' },
  },
  {
    key: 'd4',
    title: 'Design 4 — 10 Ilhas',
    subtitle: 'Alcance nacional · ênfase em cobertura geográfica',
    frente: { component: D4_FRENTE, filename: 'trabadju-D4-frente-empregador.png' },
    verso: { component: D4_VERSO, filename: 'trabadju-D4-verso-candidato.png' },
  },
  {
    key: 'd5',
    title: 'Design 5 — Sucesso',
    subtitle: 'Aspiracional · retratos editoriais',
    frente: { component: D5_FRENTE, filename: 'trabadju-D5-frente-empregador.png' },
    verso: { component: D5_VERSO, filename: 'trabadju-D5-verso-candidato.png' },
  },
  {
    key: 'd6',
    title: 'Design 6 — Dois Lados',
    subtitle: 'Foco em clareza · explica empregador vs candidato',
    frente: { component: D6_FRENTE, filename: 'trabadju-D6-frente-empregador.png' },
    verso: { component: D6_VERSO, filename: 'trabadju-D6-verso-candidato.png' },
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// INSTAGRAM TEMPLATES (Story 1080×1920 + Post 1080×1080)
// Mesmos 6 designs adaptados — reutilizam as 12 imagens IA já geradas.
// ═══════════════════════════════════════════════════════════════════════════════

const TOKENS = [
  // Design 1 — Telemóvel
  { id: 'd1f', design: 1, side: 'frente', image: '/flyers/img/01_mobile_employer.png', theme: 'navy',
    headline: 'Encontre o talento certo.', accent: 'Em minutos.',
    body: 'Publique vagas e receba candidaturas em todas as ilhas.',
    audience: 'SOU EMPREGADOR', qr: URL_EMP, qrLabel: 'Registar empresa' },
  { id: 'd1v', design: 1, side: 'verso', image: '/flyers/img/01_mobile_candidate.png', theme: 'amber',
    headline: 'O seu próximo emprego', accent: 'está aqui.',
    body: 'Pesquise, candidate-se de graça e receba alertas por email.',
    audience: 'SOU CANDIDATO', qr: URL_CAN, qrLabel: 'Criar perfil candidato' },
  // Design 2 — Carreira
  { id: 'd2f', design: 2, side: 'frente', image: '/flyers/img/02_career_employer.png', theme: 'navy',
    headline: 'Construa a sua', accent: 'equipa.',
    body: 'Pay-per-post · sem mensalidades. A partir de 1 500 CVE/anúncio.',
    audience: 'SOU EMPREGADOR', qr: URL_EMP, qrLabel: 'Registar empresa' },
  { id: 'd2v', design: 2, side: 'verso', image: '/flyers/img/02_career_candidate.png', theme: 'amber',
    headline: 'Construa o seu', accent: 'futuro.',
    body: 'Crie perfil, carregue o CV e candidate-se com um clique. Grátis.',
    audience: 'SOU CANDIDATO', qr: URL_CAN, qrLabel: 'Criar perfil candidato' },
  // Design 3 — Empresa Contrata
  { id: 'd3f', design: 3, side: 'frente', image: '/flyers/img/03_hire_employer.png', theme: 'navy',
    headline: 'Publique vagas.', accent: 'Receba candidatos.',
    body: 'Painel simples · Vinti4 e Stripe · factura PDF automática.',
    audience: 'SOU EMPREGADOR', qr: URL_EMP, qrLabel: 'Registar empresa' },
  { id: 'd3v', design: 3, side: 'verso', image: '/flyers/img/03_hire_candidate.png', theme: 'amber',
    headline: 'Candidate-se', accent: 'gratuitamente.',
    body: 'Perfil profissional · CV em PDF · candidatura em segundos.',
    audience: 'SOU CANDIDATO', qr: URL_CAN, qrLabel: 'Criar perfil candidato' },
  // Design 4 — 10 Ilhas
  { id: 'd4f', design: 4, side: 'frente', image: '/flyers/img/04_islands_employer.png', theme: 'navy',
    headline: '10 ilhas.', accent: '1 plataforma.',
    body: 'Da Praia ao Mindelo, Sal, Boa Vista, Fogo, Santo Antão, São Nicolau, Maio, Brava…',
    audience: 'SOU EMPREGADOR', qr: URL_EMP, qrLabel: 'Registar empresa' },
  { id: 'd4v', design: 4, side: 'verso', image: '/flyers/img/04_islands_candidate.png', theme: 'amber',
    headline: 'Oportunidades em', accent: 'todas as ilhas.',
    body: 'Pesquise por categoria, ilha e salário. Candidate-se em segundos.',
    audience: 'SOU CANDIDATO', qr: URL_CAN, qrLabel: 'Criar perfil candidato' },
  // Design 5 — Sucesso
  { id: 'd5f', design: 5, side: 'frente', image: '/flyers/img/05_success_employer.png', theme: 'navy',
    headline: 'Histórias de sucesso', accent: 'começam aqui.',
    body: 'Junte-se aos empregadores que confiam no Trabadju.',
    audience: 'SOU EMPREGADOR', qr: URL_EMP, qrLabel: 'Registar empresa' },
  { id: 'd5v', design: 5, side: 'verso', image: '/flyers/img/05_success_candidate.png', theme: 'amber',
    headline: 'A sua história começa', accent: 'com um clique.',
    body: 'Crie perfil, carregue o CV e candidate-se. Sempre gratuito.',
    audience: 'SOU CANDIDATO', qr: URL_CAN, qrLabel: 'Criar perfil candidato' },
  // Design 6 — Dois Lados
  { id: 'd6f', design: 6, side: 'frente', image: '/flyers/img/06_dual_employer.png', theme: 'navy',
    headline: 'Quero', accent: 'contratar.',
    body: '1. Crie conta empresa. 2. Publique a vaga. 3. Receba candidaturas.',
    audience: 'SOU EMPREGADOR', qr: URL_EMP, qrLabel: 'Registar empresa' },
  { id: 'd6v', design: 6, side: 'verso', image: '/flyers/img/06_dual_candidate.png', theme: 'amber',
    headline: 'Quero', accent: 'trabalhar.',
    body: '1. Crie perfil. 2. Carregue o CV. 3. Candidate-se — sempre grátis.',
    audience: 'SOU CANDIDATO', qr: URL_CAN, qrLabel: 'Criar perfil candidato' },
];

// Theme palette per side
const themeFor = (t) =>
  t === 'navy'
    ? { bg: COLORS.navy, accent: COLORS.amberSoft, text: COLORS.white, subText: 'rgba(255,255,255,0.92)', brandSub: COLORS.amberSoft }
    : { bg: COLORS.amber, accent: COLORS.cream, text: COLORS.white, subText: 'rgba(255,248,236,0.95)', brandSub: COLORS.cream };

// ─────────── INSTAGRAM STORY 1080×1920 (9:16) ───────────
const W_ST = 1080;
const H_ST = 1920;

const StoryCard = React.forwardRef(({ t }, ref) => {
  const p = themeFor(t.theme);
  return (
    <div
      ref={ref}
      style={{
        width: W_ST,
        height: H_ST,
        background: p.bg,
        position: 'relative',
        overflow: 'hidden',
        fontFamily: FONT,
      }}
    >
      {/* Image area — top 56% */}
      <div style={{ width: '100%', height: 1080, overflow: 'hidden', position: 'relative' }}>
        <img
          src={t.image}
          alt=""
          crossOrigin="anonymous"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              t.theme === 'navy'
                ? 'linear-gradient(180deg, rgba(11,30,68,0) 60%, rgba(11,30,68,0.95) 100%)'
                : 'linear-gradient(180deg, rgba(180,83,9,0) 60%, rgba(180,83,9,0.95) 100%)',
          }}
        />
        {/* Top safe-area brand */}
        <div style={{ position: 'absolute', top: 90, left: 90, right: 90 }}>
          <div style={{ fontSize: 64, fontWeight: 900, color: p.text, letterSpacing: '-2px', lineHeight: 1 }}>
            Trabadju
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: p.brandSub, letterSpacing: '10px', marginTop: 6 }}>
            CABO VERDE
          </div>
        </div>
      </div>

      {/* Text panel — bottom 44% */}
      <div
        style={{
          padding: '50px 90px 130px',
          color: p.text,
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
          height: 840,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 800, color: p.accent, letterSpacing: '5px' }}>
          {t.audience}
        </div>
        <div style={{ fontSize: 88, fontWeight: 900, lineHeight: 1.02, letterSpacing: '-2px' }}>
          {t.headline}<br />
          <span style={{ color: p.accent }}>{t.accent}</span>
        </div>
        <div style={{ fontSize: 28, color: p.subText, fontWeight: 500, lineHeight: 1.35 }}>
          {t.body}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, marginTop: 'auto' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, color: p.subText, fontWeight: 600 }}>
              trabadju.cv
            </div>
            <div style={{ fontSize: 24, color: p.accent, fontWeight: 800, marginTop: 4 }}>
              @trabadju.cv
            </div>
          </div>
          <div
            style={{
              background: COLORS.white,
              padding: 20,
              borderRadius: 18,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
            }}
          >
            <QRCodeSVG value={t.qr} size={210} level="H" bgColor="#ffffff" fgColor={COLORS.navy} />
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.ink, textAlign: 'center', maxWidth: 220 }}>
              {t.qrLabel}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom accent strip */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 14, background: p.accent }} />
    </div>
  );
});

// ─────────── INSTAGRAM POST 1080×1080 (1:1) ───────────
const W_P = 1080;
const H_P = 1080;

const PostCard = React.forwardRef(({ t }, ref) => {
  const p = themeFor(t.theme);
  return (
    <div
      ref={ref}
      style={{
        width: W_P,
        height: H_P,
        background: p.bg,
        position: 'relative',
        overflow: 'hidden',
        fontFamily: FONT,
        display: 'flex',
      }}
    >
      {/* Image left 45% */}
      <div style={{ width: 486, height: '100%', overflow: 'hidden', position: 'relative' }}>
        <img
          src={t.image}
          alt=""
          crossOrigin="anonymous"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              t.theme === 'navy'
                ? 'linear-gradient(90deg, rgba(11,30,68,0) 60%, rgba(11,30,68,0.85) 100%)'
                : 'linear-gradient(90deg, rgba(180,83,9,0) 60%, rgba(180,83,9,0.85) 100%)',
          }}
        />
      </div>

      {/* Text panel right 55% */}
      <div
        style={{
          width: 594,
          padding: '60px 70px 60px 50px',
          color: p.text,
          display: 'flex',
          flexDirection: 'column',
          gap: 22,
          boxSizing: 'border-box',
        }}
      >
        <div>
          <div style={{ fontSize: 46, fontWeight: 900, color: p.text, letterSpacing: '-2px', lineHeight: 1 }}>
            Trabadju
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: p.brandSub, letterSpacing: '8px', marginTop: 4 }}>
            CABO VERDE
          </div>
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: p.accent, letterSpacing: '4px' }}>
          {t.audience}
        </div>
        <div style={{ fontSize: 52, fontWeight: 900, lineHeight: 1.02, letterSpacing: '-1.5px' }}>
          {t.headline}<br />
          <span style={{ color: p.accent }}>{t.accent}</span>
        </div>
        <div style={{ fontSize: 19, color: p.subText, fontWeight: 500, lineHeight: 1.35 }}>
          {t.body}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18, marginTop: 'auto' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, color: p.subText, fontWeight: 600 }}>trabadju.cv</div>
            <div style={{ fontSize: 17, color: p.accent, fontWeight: 800, marginTop: 2 }}>@trabadju.cv</div>
          </div>
          <div
            style={{
              background: COLORS.white,
              padding: 14,
              borderRadius: 14,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            }}
          >
            <QRCodeSVG value={t.qr} size={140} level="H" bgColor="#ffffff" fgColor={COLORS.navy} />
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.ink, textAlign: 'center', maxWidth: 140 }}>
              {t.qrLabel}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

async function downloadNode(node, filename, w = W, h = H) {
  if (!node) return;
  try {
    // Wait until embedded images are loaded so they don't print blank
    const imgs = Array.from(node.querySelectorAll('img'));
    await Promise.all(
      imgs.map((img) =>
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((res) => {
              img.onload = res;
              img.onerror = res;
              setTimeout(res, 4000);
            })
      )
    );
    const dataUrl = await toPng(node, {
      pixelRatio: 1,
      cacheBust: true,
      width: w,
      height: h,
      style: { transform: 'none' },
    });
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
  } catch (e) {
    alert('Erro ao gerar imagem: ' + e.message);
  }
}

// Render a node to a PNG Blob (for ZIP packaging)
async function nodeToBlob(node, w, h) {
  if (!node) return null;
  // Wait for images to load
  const imgs = Array.from(node.querySelectorAll('img'));
  await Promise.all(
    imgs.map((img) =>
      img.complete && img.naturalWidth > 0
        ? Promise.resolve()
        : new Promise((res) => {
            img.onload = res;
            img.onerror = res;
            setTimeout(res, 4000);
          })
    )
  );
  const dataUrl = await toPng(node, {
    pixelRatio: 1,
    cacheBust: true,
    width: w,
    height: h,
    style: { transform: 'none' },
  });
  // dataUrl is like "data:image/png;base64,XXXX"
  const base64 = dataUrl.split(',')[1];
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: 'image/png' });
}

export default function FlyersPage() {
  // Build a map of refs for every render target (A5 + Story + Post)
  const refs = useMemo(() => {
    const r = {};
    DESIGNS.forEach((d) => {
      r[`${d.key}-f`] = React.createRef();
      r[`${d.key}-v`] = React.createRef();
    });
    TOKENS.forEach((t) => {
      r[`story-${t.id}`] = React.createRef();
      r[`post-${t.id}`] = React.createRef();
    });
    return r;
  }, []);

  const [zipBusy, setZipBusy] = useState(false);
  const [zipProgress, setZipProgress] = useState({ done: 0, total: 0, label: '' });

  const downloadAllAsZip = async () => {
    if (zipBusy) return;
    setZipBusy(true);
    const zip = new JSZip();
    const a5Folder = zip.folder('1_Flyer_A5_print');
    const storyFolder = zip.folder('2_Instagram_Story');
    const postFolder = zip.folder('3_Instagram_Post');

    // Build the work queue
    const queue = [];
    DESIGNS.forEach((d) => {
      queue.push({
        node: refs[`${d.key}-f`].current,
        w: W, h: H,
        folder: a5Folder,
        name: d.frente.filename,
        label: `A5 D${d.key.slice(1)} frente`,
      });
      queue.push({
        node: refs[`${d.key}-v`].current,
        w: W, h: H,
        folder: a5Folder,
        name: d.verso.filename,
        label: `A5 D${d.key.slice(1)} verso`,
      });
    });
    TOKENS.forEach((t) => {
      queue.push({
        node: refs[`story-${t.id}`].current,
        w: W_ST, h: H_ST,
        folder: storyFolder,
        name: `trabadju-D${t.design}-${t.side}-STORY.png`,
        label: `Story D${t.design} ${t.side}`,
      });
      queue.push({
        node: refs[`post-${t.id}`].current,
        w: W_P, h: H_P,
        folder: postFolder,
        name: `trabadju-D${t.design}-${t.side}-POST.png`,
        label: `Post D${t.design} ${t.side}`,
      });
    });

    setZipProgress({ done: 0, total: queue.length, label: 'A começar…' });

    try {
      for (let i = 0; i < queue.length; i++) {
        const job = queue[i];
        setZipProgress({ done: i, total: queue.length, label: job.label });
        const blob = await nodeToBlob(job.node, job.w, job.h);
        if (blob) job.folder.file(job.name, blob);
      }
      setZipProgress({ done: queue.length, total: queue.length, label: 'A comprimir…' });
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `trabadju-campanha-completa-${new Date().toISOString().slice(0, 10)}.zip`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      alert('Erro ao gerar ZIP: ' + e.message);
    } finally {
      setZipBusy(false);
      setZipProgress({ done: 0, total: 0, label: '' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-amber-50 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10" data-testid="flyers-header">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            <span className="bg-gradient-to-r from-blue-700 via-indigo-700 to-amber-600 bg-clip-text text-transparent">
              Campanha Trabadju
            </span>
          </h1>
          <p className="text-base mt-3 text-gray-600 max-w-3xl mx-auto">
            6 designs em 3 formatos: <strong>Flyer A5</strong>, <strong>Instagram Story</strong> e <strong>Instagram Post</strong> — 36 ficheiros prontos para distribuir e publicar.
          </p>
        </div>

        {/* DOWNLOAD-ALL ZIP — big CTA at the top */}
        <Card className="p-6 mb-10 bg-gradient-to-br from-blue-600 via-indigo-700 to-amber-600 border-0 shadow-xl text-white">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-xl sm:text-2xl font-extrabold leading-tight">
                Descarregar campanha completa
              </div>
              <div className="text-sm sm:text-base text-white/90 mt-1">
                36 ficheiros · 12 A5 + 12 Story + 12 Post · organizados em pastas dentro de um único <code>.zip</code> (~63 MB)
              </div>
              {zipBusy && (
                <div className="mt-3" data-testid="zip-progress">
                  <div className="text-xs text-white/90">
                    {zipProgress.done}/{zipProgress.total} · {zipProgress.label}
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2 mt-1 overflow-hidden">
                    <div
                      className="bg-white h-2 transition-all"
                      style={{ width: `${zipProgress.total ? (zipProgress.done / zipProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="/flyers/trabadju-campanha-completa.zip"
                download="trabadju-campanha-completa.zip"
                data-testid="download-zip-direct-link"
                className="inline-flex items-center justify-center bg-white text-blue-700 hover:bg-amber-50 font-extrabold h-14 px-6 text-base shadow-lg rounded-md transition"
              >
                <Archive className="h-5 w-5 mr-2" />
                Descarregar .ZIP pronto
              </a>
              <Button
                onClick={downloadAllAsZip}
                disabled={zipBusy}
                size="lg"
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/40 h-14 px-4 text-sm font-semibold"
                data-testid="download-zip-btn"
              >
                {zipBusy ? 'A gerar… aguarde' : 'Regenerar (versão fresca)'}
              </Button>
            </div>
          </div>
          <div className="mt-3 text-xs text-white/80">
            <strong>Recomendado:</strong> use «Descarregar .ZIP pronto» — começa instantaneamente. A versão «Regenerar» demora 1–3 min e cria o ZIP no seu browser.
          </div>
        </Card>

        {/* Info */}
        <Card className="p-6 mb-10 bg-white border-blue-200">
          <div className="grid md:grid-cols-3 gap-6 text-sm text-gray-700">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-base">✅ Boas práticas</h3>
              <ul className="space-y-1">
                <li>• Margem interior de segurança de <strong>~13mm</strong></li>
                <li>• Texto sobre painel sólido (alta legibilidade)</li>
                <li>• QR code gerado em tempo real — válido ao scan</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-base">🖨️ Impressão</h3>
              <ul className="space-y-1">
                <li>• Papel couché <strong>250–300g</strong> brilho ou mate</li>
                <li>• Perfil de cor <strong>CMYK</strong></li>
                <li>• Sangria de 3mm — já está no design</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-base">🔗 Destinos dos QR</h3>
              <ul className="space-y-1">
                <li>• Frente → <code className="text-xs">/register</code> (empregador)</li>
                <li>• Verso → <code className="text-xs">/candidate/register</code> (candidato)</li>
                <li>• Sempre teste antes da tiragem grande</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* ═══════════════════════ FLYER A5 ═══════════════════════ */}
        <div className="mt-4 mb-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            <span className="bg-gradient-to-r from-blue-700 via-indigo-700 to-amber-600 bg-clip-text text-transparent">
              Flyer A5 · 1748 × 2480 (300 DPI)
            </span>
          </h2>
          <p className="text-sm text-gray-600 mt-2">12 ficheiros (6 designs × frente/verso) · prontos para gráfica em Cabo Verde</p>
        </div>

        {/* Designs */}
        <div className="space-y-12">
          {DESIGNS.map((d) => {
            const F = d.frente.component;
            const V = d.verso.component;
            return (
              <Card key={d.key} className="p-6 sm:p-8 shadow-xl" data-testid={`flyer-card-${d.key}`}>
                <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">{d.title}</h2>
                    <p className="text-base text-blue-700 font-medium">{d.subtitle}</p>
                  </div>
                  <Button
                    onClick={async () => {
                      await downloadNode(refs[`${d.key}-f`].current, d.frente.filename);
                      await new Promise((r) => setTimeout(r, 500));
                      await downloadNode(refs[`${d.key}-v`].current, d.verso.filename);
                    }}
                    className="bg-gradient-to-r from-blue-600 to-amber-500 hover:from-blue-700 hover:to-amber-600 text-white font-semibold"
                    data-testid={`download-pair-${d.key}-btn`}
                  >
                    <Printer className="h-4 w-4 mr-2" /> Descarregar frente + verso
                  </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* FRENTE preview */}
                  <div>
                    <div className="text-xs uppercase font-bold text-blue-700 tracking-widest mb-2">Frente · Empregador</div>
                    <div className="border-4 border-gray-200 rounded-lg overflow-hidden shadow-lg bg-gray-100 mx-auto"
                         style={{ width: '100%', maxWidth: '500px', aspectRatio: `${W}/${H}` }}>
                      <div style={{
                        width: W, height: H,
                        transform: 'scale(0.286)',
                        transformOrigin: 'top left',
                        pointerEvents: 'none',
                      }}>
                        <F />
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => downloadNode(refs[`${d.key}-f`].current, d.frente.filename)}
                      className="w-full mt-3 border-blue-300"
                      data-testid={`download-${d.key}-frente-btn`}
                    >
                      <Download className="h-4 w-4 mr-2" /> Só a frente
                    </Button>
                  </div>

                  {/* VERSO preview */}
                  <div>
                    <div className="text-xs uppercase font-bold text-amber-700 tracking-widest mb-2">Verso · Candidato</div>
                    <div className="border-4 border-gray-200 rounded-lg overflow-hidden shadow-lg bg-gray-100 mx-auto"
                         style={{ width: '100%', maxWidth: '500px', aspectRatio: `${W}/${H}` }}>
                      <div style={{
                        width: W, height: H,
                        transform: 'scale(0.286)',
                        transformOrigin: 'top left',
                        pointerEvents: 'none',
                      }}>
                        <V />
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => downloadNode(refs[`${d.key}-v`].current, d.verso.filename)}
                      className="w-full mt-3 border-amber-300"
                      data-testid={`download-${d.key}-verso-btn`}
                    >
                      <Download className="h-4 w-4 mr-2" /> Só o verso
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Off-screen full-size render targets (high-DPI download source) */}
        <div aria-hidden style={{ position: 'fixed', left: '-20000px', top: 0, pointerEvents: 'none', opacity: 0 }}>
          {DESIGNS.map((d) => {
            const F = d.frente.component;
            const V = d.verso.component;
            return (
              <React.Fragment key={d.key}>
                <F ref={refs[`${d.key}-f`]} />
                <V ref={refs[`${d.key}-v`]} />
              </React.Fragment>
            );
          })}
          {/* Off-screen Story + Post (for full-resolution downloads) */}
          {TOKENS.map((t) => (
            <React.Fragment key={`og-${t.id}`}>
              <StoryCard t={t} ref={refs[`story-${t.id}`]} />
              <PostCard t={t} ref={refs[`post-${t.id}`]} />
            </React.Fragment>
          ))}
        </div>

        {/* ═══════════════════════ INSTAGRAM STORY ═══════════════════════ */}
        <div className="mt-20 mb-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            <span className="bg-gradient-to-r from-pink-500 via-fuchsia-600 to-amber-500 bg-clip-text text-transparent">
              Instagram Story · 1080 × 1920
            </span>
          </h2>
          <p className="text-sm text-gray-600 mt-2">12 visuais (6 designs × frente/verso) · formato 9:16 para Stories e Reels</p>
        </div>
        <Card className="p-4 sm:p-6 shadow-xl">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {TOKENS.map((t) => (
              <div key={`story-${t.id}`} className="flex flex-col" data-testid={`story-card-${t.id}`}>
                <div className="text-xs font-bold uppercase tracking-widest mb-1.5"
                     style={{ color: t.theme === 'navy' ? '#1d4ed8' : '#b45309' }}>
                  D{t.design} · {t.side === 'frente' ? 'Empregador' : 'Candidato'}
                </div>
                <div className="border-2 border-gray-200 rounded-lg overflow-hidden shadow-md bg-gray-100"
                     style={{ width: '100%', aspectRatio: `${W_ST}/${H_ST}` }}>
                  <div style={{
                    width: W_ST, height: H_ST,
                    transform: 'scale(0.22)',
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                  }}>
                    <StoryCard t={t} />
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadNode(refs[`story-${t.id}`].current,
                    `trabadju-D${t.design}-${t.side}-STORY.png`, W_ST, H_ST)}
                  className="w-full mt-2"
                  data-testid={`download-story-${t.id}-btn`}
                >
                  <Download className="h-3 w-3 mr-1.5" /> Story PNG
                </Button>
              </div>
            ))}
          </div>
          <Button
            onClick={async () => {
              for (const t of TOKENS) {
                await downloadNode(refs[`story-${t.id}`].current,
                  `trabadju-D${t.design}-${t.side}-STORY.png`, W_ST, H_ST);
                await new Promise((r) => setTimeout(r, 400));
              }
            }}
            className="w-full mt-6 bg-gradient-to-r from-pink-500 to-amber-500 hover:from-pink-600 hover:to-amber-600 text-white font-semibold"
            data-testid="download-all-stories-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Descarregar todas as 12 Stories
          </Button>
        </Card>

        {/* ═══════════════════════ INSTAGRAM POST ═══════════════════════ */}
        <div className="mt-20 mb-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            <span className="bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 bg-clip-text text-transparent">
              Instagram Post · 1080 × 1080
            </span>
          </h2>
          <p className="text-sm text-gray-600 mt-2">12 visuais (6 designs × frente/verso) · formato 1:1 para o feed</p>
        </div>
        <Card className="p-4 sm:p-6 shadow-xl">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {TOKENS.map((t) => (
              <div key={`post-${t.id}`} className="flex flex-col" data-testid={`post-card-${t.id}`}>
                <div className="text-xs font-bold uppercase tracking-widest mb-1.5"
                     style={{ color: t.theme === 'navy' ? '#1d4ed8' : '#b45309' }}>
                  D{t.design} · {t.side === 'frente' ? 'Empregador' : 'Candidato'}
                </div>
                <div className="border-2 border-gray-200 rounded-lg overflow-hidden shadow-md bg-gray-100"
                     style={{ width: '100%', aspectRatio: '1/1' }}>
                  <div style={{
                    width: W_P, height: H_P,
                    transform: 'scale(0.28)',
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                  }}>
                    <PostCard t={t} />
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadNode(refs[`post-${t.id}`].current,
                    `trabadju-D${t.design}-${t.side}-POST.png`, W_P, H_P)}
                  className="w-full mt-2"
                  data-testid={`download-post-${t.id}-btn`}
                >
                  <Download className="h-3 w-3 mr-1.5" /> Post PNG
                </Button>
              </div>
            ))}
          </div>
          <Button
            onClick={async () => {
              for (const t of TOKENS) {
                await downloadNode(refs[`post-${t.id}`].current,
                  `trabadju-D${t.design}-${t.side}-POST.png`, W_P, H_P);
                await new Promise((r) => setTimeout(r, 400));
              }
            }}
            className="w-full mt-6 bg-gradient-to-r from-indigo-500 to-pink-500 hover:from-indigo-600 hover:to-pink-600 text-white font-semibold"
            data-testid="download-all-posts-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Descarregar todos os 12 Posts
          </Button>
        </Card>

        {/* ═══════════════════════ VÍDEOS (Sora 2) ═══════════════════════ */}
        <div className="mt-20 mb-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            <span className="bg-gradient-to-r from-purple-600 via-fuchsia-600 to-rose-500 bg-clip-text text-transparent">
              Vídeos publicitários · IA Sora 2
            </span>
          </h2>
          <p className="text-sm text-gray-600 mt-2">2 vídeos 720×1280 (9:16) · 8 segundos · prontos para Instagram Story / Reels / TikTok</p>
          <p className="text-xs text-gray-500 mt-1">💡 Para Posts quadrados, recorte para 1:1 no CapCut ou Canva.</p>
        </div>
        <Card className="p-6 shadow-xl">
          <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="flex flex-col items-center" data-testid="video-empregador">
              <div className="text-xs font-bold uppercase tracking-widest mb-2 text-blue-700">
                Empregador · Story 9:16
              </div>
              <video
                src="/flyers/videos/trabadju-empregador-story.mp4"
                controls
                loop
                muted
                playsInline
                className="rounded-xl shadow-lg w-full bg-black"
                style={{ aspectRatio: '9/16', maxWidth: '280px' }}
              />
              <a
                href="/flyers/videos/trabadju-empregador-story.mp4"
                download="trabadju-empregador-story.mp4"
                className="inline-flex items-center justify-center mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition"
                data-testid="download-video-empregador-btn"
              >
                <Download className="h-4 w-4 mr-2" /> Descarregar MP4
              </a>
            </div>
            <div className="flex flex-col items-center" data-testid="video-candidato">
              <div className="text-xs font-bold uppercase tracking-widest mb-2 text-amber-700">
                Candidato · Story 9:16
              </div>
              <video
                src="/flyers/videos/trabadju-candidato-story.mp4"
                controls
                loop
                muted
                playsInline
                className="rounded-xl shadow-lg w-full bg-black"
                style={{ aspectRatio: '9/16', maxWidth: '280px' }}
              />
              <a
                href="/flyers/videos/trabadju-candidato-story.mp4"
                download="trabadju-candidato-story.mp4"
                className="inline-flex items-center justify-center mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition"
                data-testid="download-video-candidato-btn"
              >
                <Download className="h-4 w-4 mr-2" /> Descarregar MP4
              </a>
            </div>
          </div>
          <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg text-sm text-gray-700">
            <strong>💡 Próximo passo:</strong> abra o vídeo no CapCut ou Canva e adicione: (1) logo Trabadju no canto superior esquerdo, (2) headline animado (ex: «Quero contratar.» / «Quero trabalhar.»), (3) QR code + URL nos últimos 2s. Mais tarde podemos gerar mais vídeos quando adicionar saldo ao Universal Key.
          </div>
        </Card>

        {/* Footer tip */}
        <Card className="p-6 mt-12 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-gray-900 mb-2 text-base">📱 Antes de imprimir ou publicar…</h3>
          <p className="text-sm text-gray-700">
            Descarregue um ficheiro, abra no computador e <strong>scaneie o QR code</strong>. Tem de abrir <code>{URL_HOME}</code> (ou as páginas de registo). Imprima sempre <strong>1 cópia de teste</strong> antes da tiragem final.
          </p>
        </Card>
      </div>
    </div>
  );
}
